import { pipe } from 'fp-ts/function';
import { array, ioOption } from 'fp-ts';
import { useQueryClient } from 'react-query';

import { ApiRequest } from 'services/api';
import { useRestMutation } from 'utils/hooks/api/useRestMutation';

import { Channel } from '../../domain';
import { UserMetadataQueryResponse } from '../../hooks/useUserMetadataQuery';
import { getMetadata, mergeMetadata, updateMetadata } from './utils';

const SUBSCRIBE_TO_CHANNEL_API = '/users/profiles/subscribeUserToChannel';

type SubscribeUserToChannelMutationRequest = {
  channelIds: Channel['id'][];
  userKey: string;
};

const removeFromUnsubscribed =
  (updatedChannels: Channel['id'][]) =>
  (value: UserMetadataQueryResponse): Partial<UserMetadataQueryResponse> =>
    pipe(
      value.unsubscribedChannelIds,
      array.filter((channelId) => !updatedChannels.includes(channelId)),
      (unsubscribedChannelIds) => ({ unsubscribedChannelIds }),
    );

export const useSubscribeUserToChannelMutation = (defaultConfig?: ApiRequest) => {
  const queryClient = useQueryClient();
  return useRestMutation<unknown, SubscribeUserToChannelMutationRequest>(
    SUBSCRIBE_TO_CHANNEL_API,
    undefined,
    {
      onSuccess: (_data, { data }) => {
        const program = pipe(
          data.userKey,
          getMetadata(queryClient), // get Metadata saved in query cache
          ioOption.fromNullable,
          ioOption.map(mergeMetadata(removeFromUnsubscribed(data.channelIds))), // if we have data, mutate it by merging partial updates
          ioOption.tapIO(updateMetadata(data.userKey, queryClient)), // if we have data, mutate what is saved in query cache
        );

        program(); // Because we're using the IO type, we have to run the pipeline
      },
    },
    defaultConfig,
  );
};
